<template>
  <main class='recipient-dropdown'>
    <sb-async-dropdown
      :selected="selected"
      :headerRows="headerRows"
      :rows="rows"
      :footerRows="footerRows"
      :focused="focused"
      @selectedRow="selectedRow"
      @deselectedRow="deselectedRow"
      @update:focused="updateFocused"
    >
      <template #label-only>
        <div class="recipient-label">
          <div>recipient filters</div>
          (<counter :start="prevRecipientCount" :end="totalRecipientCount" />)
        </div>
      </template>
    </sb-async-dropdown>

    <div class="preview">
      <template v-for="(entry, index) in previewDatum">
        <span :key="entry.name">
          <span class='bold preview-name' style='margin-right:15px'>
            {{ entry.name }}
            <span v-if="entry.includeDateRange && hasDateRange">({{ startStr }} to {{ endStr }})</span>
            :
          </span>

          <span class='preview-labels' :class="{ 'not-last': index !== previewDatum.length - 1 }">
            {{ entry.labels.join(', ') }}
          </span>
        </span>
      </template>
    </div>
  </main>
</template>

<script>
import {campaignAPI} from "../../services/api/campaignAPI";
import {groupsAPI, isSegmentChild, isTagChild} from "../../services/api/groupsAPI";
import {debounce} from 'lodash';
import {format} from 'date-fns';
import {Counter} from "@springbig/sb-components";

const tagContextToPatchKeys = {
  'brands': 'brand_list',
  'categories': 'category_list',
  'interests': 'interest_list',
  'discounts': 'discount_list',
  'custom_groups': 'custom_group_list'
};

const groupsWithDateFilter = [
  'spending total', 'visiting total', 'average spend', 'order type',
];

export default {
  name: "recipient-dropdown",
  props: {
    campaign: Object,
    countAsEmail: Boolean,
  },
  components: {
    Counter
  },
  data() {
    return {
      loadingTotalCount: false,
      prevRecipientCount: 0,
      totalRecipientCount: 0,
      focused: false,
      childMap: new Map(),
      rootRows: [],
      previewDatum: [], // Array of tuples (group.name, Array.<string>)
      currentGroup: null,
      selected:[], //Holds all selected rows across all groups
      selectedLabelMap: new Map(), //Holds all selected label sets index by their group
      selectedLabelSet: null, //Holds all selected labels for the currently rendered group (is null at root)
      selectedMap: new Map(), //Holds all selected sets indexed by their group
      selectedSet: null, //Holds all selected items for the currently rendered group (is null at root)
      start: null,
      end: null,
      rows: [],
      headerRows: [],
      footerRows: [],
      selectedLength: 0
    }
  },
  async created() {
    const { groups_with_selected: groups } = await campaignAPI.selectedGroups(this.campaign.id);

    this.rootRows = groups.map((group) => ({
      slot: 'parent-row',
      state: {
        context: group,
        label: group.name,
        onClick: () => {
          this.currentGroup = group;

          this.headerRows = [
            {
              slot: 'go-back-row',
              state: {
                label: group.name,
                onClick: () => {
                  this.renderRoot();
                }
              }
            }, {
              slot: 'query-row',
              state: {
                value: '',
                update: debounce((newQuery) => {
                  //API is not zero-indexed
                  this.navigateRecipientComponent({ group, page: 1, query: newQuery })
                }, 500)
              }
            }
          ];
          //API is not zero-indexed
          this.navigateRecipientComponent({ group, page: 1, query: '' });
        }
      }
    }));

    groups.forEach((group) => {
      this.selectedLabelMap.set(group.name, new Set(group.display_names));
    });

    this.refreshTotalCount();
    this.renderRoot();
    this.updatePreview();
  },
  computed: {
    updateGroups() {
      return debounce(async (frag = {}) => {
        if(this.hasDateRange && frag.merchant_segment_ids && !frag.date_range) {
          frag.date_range = `${this.startStr} to ${this.endStr}`;
        }

        if(Object.keys(frag).length > 0) {
          await campaignAPI.updateGroups(this.campaign.id, frag);
        }

        this.refreshTotalCount();
        this.updatePreview();
      }, 1000);
    },
    hasDateRange() {
      return this.start && this.end;
    },
    startStr() {
      return format(this.start, 'yyyy-MM-dd');
    },
    endStr() {
      return format(this.end, 'yyyy-MM-dd');
    }
  },
  watch: {
    countAsEmail() {
      this.refreshTotalCount();
    }
  },
  methods: {
    updateDateWithSegments(value) {
      const { merchant_segment_ids } = this.getUpdateGroupsParams();
      this.updateGroups({
        merchant_segment_ids,
        date_range: value
      });
    },
    async refreshTotalCount() {
      this.loadingTotalCount = true;

      const recipientCount = await campaignAPI.totalRecipientCount(this.campaign.id, this.countAsEmail);
      this.prevRecipientCount = this.totalRecipientCount;
      this.totalRecipientCount = recipientCount;

      this.loadingTotalCount = false;
    },
    async navigateRecipientComponent(options) {
      const { group, page, query } = options;
      this.rows = [
        'loading-row'
      ];

      if(!this.selectedMap.has(group.name)) {
        this.selectedMap.set(group.name, new Set());
      }

      this.selectedSet = this.selectedMap.get(group.name);
      this.selectedLabelSet = this.selectedLabelMap.get(group.name);

      const resp = await groupsAPI.getGroupChildren(group, page, query);
      this.rows = this.createChildRows(resp.children);

      if(this.rows.length === 0) {
        this.rows = [
          {
            slot: 'text-row',
            state: {
              message: 'group not configured'
            }
          }
        ];
      }

      this.footerRows = [];

      if(groupsWithDateFilter.includes(this.currentGroup.name)) {
        this.footerRows.push({
          slot: 'sub-filter-row',
          state: {
            focused: this.hasDateRange,
            label: "add sub filters",
            filters: [
              {
                type: 'date-range',
                label: "select date range",
                start: this.start,
                end: this.end,
                maxDate: new Date(),
                update: (range) => {
                  if(!range) {
                    this.start = null;
                    this.end = null;
                    this.updateDateWithSegments(null)
                  } else if(range.start && range.end) {
                    this.start = range.start;
                    this.end = range.end;
                    this.updateDateWithSegments(`${this.startStr} to ${this.endStr}`)
                  }
                }
              }
            ]
          }
        });
      }

      if(resp.pagination.total_pages > 1) {
        this.footerRows.push(({
          slot: 'pagination-row',
          state: {
            page: page - 1, //API is not zero-indexed
            pages: resp.pagination.total_pages,
            gap: 2,
            variant: 'dotted',
            update: (newPage) => {
              //API is not zero-indexed
              this.navigateRecipientComponent({ group, page: newPage + 1, query })
            }
          }
        }));
      }
    },
    updateFocused(focused) {
      this.focused = focused;

      if(!focused) {
        this.renderRoot();
      }
    },
    createChildRows(children) {
      const hasMeta = this.$store.getters.advancedSegmentEnabled
      const rows = children.filter((c) => {
        return c.meta_name == null || hasMeta
      }).map((child) => {
        if(!this.childMap.has(child.id)) {
          this.childMap.set(child.id, ({
            slot: 'toggle-row',
            state: {
              id: child.id,
              context: child,
              label: child.label,
              subText: '-- texts, -- emails',
              fetchedSubtext: false
            }
          }));
        }

        return this.childMap.get(child.id);
      });

      rows.forEach(async (row) => {
        if(this.selectedLabelSet.has(row.state.context.label) && !this.selectedSet.has(row.state)) {
          this.selectedSet.add(row.state);
          this.selected.push(row.state);
        }

        if(!row.state.fetchedSubtext) {
          row.state.fetchedSubtext = true;
          let result = await groupsAPI.count(row.state.context);
          row.state.subText = `${result.sms} texts, ${result.email} emails`;
        }
      });

      return rows;
    },
    selectedRow(state) {
      this.selectedSet.add(state);
      this.selectedLabelSet.add(state.label);
      this.selected.push(state);

      this.didChangeSelected(state);
    },
    deselectedRow(state) {
      this.selectedSet.delete(state);
      this.selectedLabelSet.delete(state.label);
      this.selected = this.selected.filter((entry) => entry !== state);

      this.didChangeSelected(state);
    },
    async didChangeSelected(state) {
      const params = this.getUpdateGroupsParams();

      let key
      if(isSegmentChild(state.context)) {
        key = 'merchant_segment_ids';
      } else if(isTagChild(state.context)) {
        key = tagContextToPatchKeys[state.context.context];
      }

      await this.updateGroups({ [key]: params[key] });
    },
    getUpdateGroupsParams() {
      //Build out the params
      const params = this.selected.reduce((result, state) => {
        if(isSegmentChild(state.context)) {
          result.merchant_segment_ids.push(state.context.id);
        } else if(isTagChild(state.context)) {
          const key = tagContextToPatchKeys[state.context.context];

          if(!result[key]) {
            result[key] = [];
          }

          result[key].push(state.context.tag);
        }

        return result;
      }, {
        merchant_segment_ids: [],
        brand_list: [],
        category_list: [],
        interest_list: [],
        discount_list: [],
        custom_group_list: []
      });

      //Map over params once last time to transform merchant taggings into comma delimited list
      const patchKeys = Object.values(tagContextToPatchKeys);
      for(const key in params) {
        if(patchKeys.includes(key)) {
          params[key] = params[key].join(',');
        }
      }
      return params
    },
    updatePreview() {
      const datum = [];

      this.selectedLabelMap.forEach((selectedSet, groupName) => {
        const labels = Array.from(selectedSet.values())

        if(labels.length > 0) {
          datum.push({
            name: groupName,
            labels: labels,
            includeDateRange: groupsWithDateFilter.includes(groupName)
          });
        }
      });

      this.previewDatum = datum;
    },
    renderRoot() {
      this.rootRows.forEach((row) => {
        const selectedSet = this.selectedMap.get(row.state.context.name) || this.selectedLabelMap.get(row.state.context.name);
        const size = selectedSet?.size;
        row.state.badge = size === 0 ? undefined : size;
      })

      this.headerRows = [];
      this.rows = this.rootRows;
      this.footerRows = [];
      this.selectedSet = null;
      this.selectedLabelSet = null;
    }
  }
}
</script>

<style scoped>
  .preview-name {
    margin-right: 15px;
  }

  .preview-labels.not-last{
    border-right: 1px solid #128ff9;
    padding-right: 10px;
    margin-right: 10px;
  }

  .recipient-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
  }

  .recipient-label {
    font-family: AileronThin;
    line-height: 1.6rem;
  }

  .recipient-dropdown {
    display: flex;
    flex-wrap: nowrap;
    border: 1px solid #128ff9;
    border-radius: 16px;
  }

  .preview {
    padding: 5px;
  }

  .recipient-dropdown >>> .sb-async-dropdown {
    margin:-1px;
  }

  .recipient-dropdown >>> .sb-async-dropdown.focused {
    min-width: 260px;
  }

  .recipient-dropdown > .sb-async-dropdown.focused >>> .history-container {
    font-size: 15px;
  }

  .recipient-dropdown >>> .sb-async-dropdown .sb-content {
    right: 0;
    left: 0;
  }
  .recipient-dropdown >>> .label-container {
    border-radius: 16px;
  }
  .recipient-dropdown .sb-async-dropdown.focused >>> .label-container {
    border-radius: 16px 16px 0 0;
  }

  .recipient-dropdown .sb-async-dropdown.focused >>> .label-container.flatpickr-input {
    border-radius: 16px;
    font-size: 15px;
  }
  .recipient-dropdown .sb-async-dropdown.focused >>> .sb-content {
    border-radius: 0 0 16px 16px;
  }
</style>
